// P1: Match by reference number found in line item text.

import type { MatchCandidate, MatchLineItem, NearMissCandidate, UnmatchedReason, UsedCandidates, VerifyResult } from './types.ts';
import type { InvoiceLink } from '@shared/types/invoice.ts';
import { verifySingleMatch, verifyMultiMatch } from './verify.ts';
import { isUsed, markUsed } from './candidates.ts';
import { containsRef, extractRefs } from '../references.ts';
import { candidateAbsNet } from '../../../calc.ts';

export interface P1Result {
  links: InvoiceLink[];
  verified: boolean;
  verifyResult?: VerifyResult;
  /** Whether P1 found refs in candidates (blocks P2 fallthrough). */
  refsBlockFallthrough: boolean;
  /** Candidates that ref-matched but were dropped (verify failed or currency mismatch). */
  nearMisses?: NearMissCandidate[];
  /** True when `links` are emitted despite a failed verify because every mismatch is
   *  amount-only (net/vat/gross/unit_price/quantity) — the invoice needs a correction. */
  correctionRequired?: boolean;
  /** A more specific unmatched reason than the caller's default when P1 diagnosed one
   *  (ref_found_tx_unpriced / ref_linked_elsewhere / candidate_unweighed). */
  refReason?: UnmatchedReason;
}

/** Whether a candidate is per-weight priced but carries no usable weight → its net is 0.
 *  Such a candidate is OUR incomplete data (an unweighed transaction), not a vendor error. */
function isUnweighedPerTon(c: MatchCandidate): boolean {
  return c.price_type === 'per_ton' && (c.weight == null || c.weight === 0);
}

/** Diagnose why a ref in the line text matched nothing in the working candidate pool, using
 *  the full vendor lookup (finding 6). Priority: linked-elsewhere > unweighed > unpriced.
 *  Returns undefined when no lookup is provided or nothing carries the ref (→ the caller's
 *  default p1_ref_unmatched / no_candidates stands). */
function diagnoseRefMiss(
  refsInText: string[],
  invoiceId: number,
  refLookup: MatchCandidate[] | undefined,
): UnmatchedReason | undefined {
  if (!refLookup || refLookup.length === 0) return undefined;
  const hits = refLookup.filter(c => {
    const candRef = c.reference_number;
    // Must exclude '' as well as null: containsRef('') builds an always-true
    // regex, which would fabricate an unmatched-reason in the report.
    return candRef != null && candRef !== '' && refsInText.some(r => containsRef(candRef, r) || containsRef(r, candRef));
  });
  if (hits.length === 0) return undefined;
  // A side linked to a DIFFERENT invoice — the ref's transaction is already invoiced.
  if (hits.some(c => c.linked_invoice_id != null && c.linked_invoice_id !== invoiceId)) {
    return 'ref_linked_elsewhere';
  }
  // A per-ton transaction with no weight → net 0, excluded from the priced pool.
  if (hits.some(isUnweighedPerTon)) return 'candidate_unweighed';
  // A side excluded because its price is null/0.
  if (hits.some(c => c.unit_price == null || c.unit_price === 0 || candidateAbsNet(c.unit_price, c.price_type, c.weight) === 0)) {
    return 'ref_found_tx_unpriced';
  }
  return undefined;
}

/** Fields whose mismatch means "the invoice amounts are wrong" → correctable by a
 *  vendor correction. A mismatch on anything else (date, vat_rate_code, candidates)
 *  is structural and must NOT be auto-linked. Indexed forms (`unit_price[2]`) included. */
const AMOUNT_MISMATCH_FIELDS = new Set(['net_amount', 'vat_amount', 'gross_amount', 'unit_price', 'quantity']);

function isAmountMismatch(field: string): boolean {
  return AMOUNT_MISMATCH_FIELDS.has(field.replace(/\[\d+\]$/, ''));
}

/**
 * Match by reference number found in line item name/description.
 * Returns null if no refs detected in text at all.
 */
export function matchByReference(
  invoiceId: number,
  li: MatchLineItem,
  candidates: MatchCandidate[],
  currency: string,
  sellDate: string | null | undefined,
  used: UsedCandidates,
  allowAdjacentMonth = false,
  /** OPTIONAL full vendor pool (free + own + taken-by-other + unpriced) used ONLY for
   *  diagnosing a ref that matched nothing in `candidates`: distinguishes a ref linked to
   *  another invoice (ref_linked_elsewhere) or an excluded-because-unpriced transaction
   *  (ref_found_tx_unpriced) from a plain p1_ref_unmatched. When omitted, diagnosis is
   *  skipped and the caller's default reason stands. */
  refLookup?: MatchCandidate[],
): P1Result | null {
  const tag = `[P1:inv${invoiceId}:li${li.id}]`;
  // The line's own reference (attached for single-reference invoices) is scanned alongside
  // the free text — a candidate carrying it links exactly as a ref named in the text would.
  const text = [li.name, li.description, li.reference_number].filter(Boolean).join(' ');
  if (!text) { console.log(`${tag} no text — skip`); return null; }

  const candidatesWithRef = candidates.filter(c => !isUsed(used, c) && c.reference_number);
  console.log(`${tag} scanning "${text.substring(0, 80)}" against ${candidates.length} candidates (${candidatesWithRef.length} with refs)`, {
    refs: candidatesWithRef.map(c => ({ id: c.entity_id, ref: c.reference_number, type: c.entity_type, side: c.side, currency: c.currency })),
  });

  const links: InvoiceLink[] = [];
  const matchedCandidates: MatchCandidate[] = [];
  const currencyMismatchCandidates: MatchCandidate[] = [];
  let hasSkippedRefsInCandidates = false;
  let hasAnyRefHit = false;

  for (const c of candidates) {
    if (isUsed(used, c) || !c.reference_number) continue;
    if (!containsRef(text, c.reference_number)) continue;
    hasAnyRefHit = true;
    console.log(`${tag} ref hit: ${c.reference_number} → entity ${c.entity_type}:${c.entity_id} (currency=${c.currency})`);

    if (!c.currency || c.currency !== currency) {
      console.log(`${tag} currency mismatch: candidate=${c.currency} invoice=${currency} — skipped`);
      hasSkippedRefsInCandidates = true;
      currencyMismatchCandidates.push(c);
      continue;
    }

    links.push({ ...c, invoice_id: invoiceId, invoice_line_item_id: li.id });
    matchedCandidates.push(c);
    // markUsed deferred — only verified P1 matches claim candidates so a
    // failed-verify suggestion on one invoice doesn't poison `sharedUsed`
    // for other invoices in the batch.
  }

  if (!hasAnyRefHit) {
    const refsInText = extractRefs(text);
    if (refsInText.length === 0) { console.log(`${tag} no ref hits at all — null`); return null; }
    // Line item text carries a ref → P1 owns it. Block P2 fallthrough so we don't
    // amount-match the wrong transaction when the named one is excluded from the
    // candidate pool (e.g. already linked to this same invoice on a re-reconcile).
    // Diagnose WHY the named ref isn't in the pool, if the full vendor lookup is provided.
    const refReason = diagnoseRefMiss(refsInText, invoiceId, refLookup);
    console.log(`${tag} refs in text not matched in candidates: ${refsInText.join(', ')} — block P2 (reason=${refReason ?? 'p1_ref_unmatched'})`);
    return { links: [], verified: false, refsBlockFallthrough: true, refReason };
  }

  // Finding 4: a ref-matched candidate that is per-ton priced with a null/0 weight has
  // net 0 → verify would report a large "amount mismatch" and (pre-fix) FIRM-link it with
  // request_correction, wrongly blaming the vendor for OUR unweighed transaction. Instead:
  // emit candidate_unweighed, do NOT link, do NOT flag correction, and MARK the candidate
  // used so P2 doesn't consume it either. (11 of 21 dev correction requests were this.)
  const unweighed = matchedCandidates.filter(isUnweighedPerTon);
  if (unweighed.length > 0) {
    for (const c of unweighed) markUsed(used, c);
    const nearMisses: NearMissCandidate[] = unweighed.slice(0, 3).map(c => ({
      type: c.entity_type,
      entity_id: c.entity_id,
      side: c.side ?? null,
      reference_number: c.reference_number ?? null,
      net_amount: 0,
      date: c.date ?? null,
      weight: c.weight ?? null,
      disqualified_by: `candidate_unweighed: per_ton transaction has no weight (net=0)`,
    }));
    console.log(`${tag} ref hit but candidate unweighed (per_ton, weight null/0) — no link, complete tx data`);
    return { links: [], verified: false, refsBlockFallthrough: true, nearMisses, refReason: 'candidate_unweighed' };
  }

  if (links.length === 0 && !hasSkippedRefsInCandidates) {
    console.log(`${tag} ref hits but no links, no skips — null`);
    return null;
  }

  // REC-m10: all ref-hits were skipped for currency — surface currency_no_candidates
  // distinctly (a ref matched but the vendor has no same-currency candidate for it).
  const currencyOnlyMiss = links.length === 0 && hasSkippedRefsInCandidates;

  // If all ref-hits were skipped due to currency mismatch, surface them as near-misses
  // (returned below after verify block is skipped because links.length === 0).
  const currencyNearMisses: NearMissCandidate[] = currencyMismatchCandidates.slice(0, 3).map(c => ({
    type: c.entity_type,
    entity_id: c.entity_id,
    side: c.side ?? null,
    reference_number: c.reference_number ?? null,
    net_amount: c.pricing?.net_amount ?? null,
    date: c.date ?? null,
    weight: c.weight ?? null,
    disqualified_by: `currency_mismatch: candidate=${c.currency}, invoice=${currency}`,
  }));

  // Verify amounts + date
  let verified = false;
  let verifyResult: VerifyResult | undefined;
  if (links.length > 0 && links.every(l => l.pricing.net_amount !== null)) {
    // A13: verify must compare against the RAW candidate vat_rate_code, NOT the enriched
    // pricing's — calculatePricing (via enrichInvoiceLink) defaults a null code to '23',
    // which would let a code-less transaction spuriously verify against a '23' invoice
    // line. P2's filterCandidates already gates on the raw column (normVat, no default);
    // P1 verify now matches that semantics by overriding vat_rate_code with the raw value.
    const positions = links.map(l => ({ ...l.pricing, vat_rate_code: l.vat_rate_code }));
    const dates = matchedCandidates.map(c => c.date);
    const weights = matchedCandidates.map(c => c.weight);
    verifyResult = positions.length === 1
      ? verifySingleMatch(li, positions[0], sellDate, dates[0], weights[0], allowAdjacentMonth)
      : verifyMultiMatch(li, positions, sellDate, dates, weights, allowAdjacentMonth);
    verified = verifyResult.verified;
    if (verified) {
      // Confirmed P1 match — claim candidates so subsequent line items in
      // this invoice and other invoices in the batch don't double-match them.
      for (const c of matchedCandidates) markUsed(used, c);
    } else {
      const mismatchDesc = verifyResult.mismatches.map(m => `${m.field}:expected=${m.expected},actual=${m.actual}`).join('; ');
      // Amount-only mismatch (net/vat/gross/unit_price/quantity) → the reference is
      // trustworthy but the invoice numbers are wrong. Emit FIRM links and flag the
      // invoice for a vendor correction instead of dropping the match. Hard mismatches
      // (date / vat_rate_code / candidates) stay dropped — those aren't a simple correction.
      const amountOnly = verifyResult.mismatches.every(m => isAmountMismatch(m.field));
      if (amountOnly) {
        console.log(`${tag} verify failed but amount-only — linking ${links.length} with correction required (${mismatchDesc})`);
        for (const c of matchedCandidates) markUsed(used, c);
        return {
          links,
          verified: false,
          verifyResult,
          refsBlockFallthrough: true,
          correctionRequired: true,
        };
      }
      // Hard mismatch — don't emit links; caller gets empty links + verifyResult.
      console.log(`${tag} verify failed (hard mismatch) — dropping ${links.length} links, no link emitted`);
      const nearMisses: NearMissCandidate[] = matchedCandidates.slice(0, 3).map(c => ({
        type: c.entity_type,
        entity_id: c.entity_id,
        side: c.side ?? null,
        reference_number: c.reference_number ?? null,
        net_amount: c.pricing?.net_amount ?? null,
        date: c.date ?? null,
        weight: c.weight ?? null,
        disqualified_by: `p1_verify_failed: ${mismatchDesc}`,
      }));
      return {
        links: [],
        verified: false,
        verifyResult,
        refsBlockFallthrough: true,
        nearMisses,
      };
    }
    console.log(`${tag} verify: verified=${verified}, mismatches=${verifyResult.mismatches.map(m => m.field).join(',') || 'none'}`);
  }

  console.log(`${tag} result: ${links.length} links, verified=${verified}, blockFallthrough=${links.length > 0 || hasSkippedRefsInCandidates}`);
  return {
    links,
    verified,
    verifyResult,
    refsBlockFallthrough: links.length > 0 || hasSkippedRefsInCandidates,
    // Surface currency-mismatch near-misses when we had ref hits but no links
    nearMisses: links.length === 0 ? currencyNearMisses : undefined,
    refReason: currencyOnlyMiss ? 'currency_no_candidates' : undefined,
  };
}
